/**
 * ResultPanel — displays the outcome of a demo action.
 *
 * Single responsibility: render a DemoResult with appropriate
 * visual treatment (color, icon, expandable explanation).
 *
 * Icons are typed `lucide-react` glyphs (not Unicode emoji) so the
 * surface renders identically across platforms and the icons inherit
 * the tone's foreground color via `currentColor`.
 *
 * When a result transitions from `null` → present, or from one result
 * to another, the panel plays the shared `entranceVariants` from
 * `motionTokens` so the new outcome reads as an event arriving rather
 * than a static surface update. `prefers-reduced-motion` collapses this
 * to a no-op via `useReducedMotion`, and an `AnimatePresence` wrapper
 * preserves an exit animation when consumers clear the result.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { AlertTriangle, CheckCircle2, Info, type LucideIcon } from "lucide-react";
import type { DemoResult, ResultType } from "../types.js";
import { FONT, GRAY, RESULT_STYLE } from "../design-system/tokens.js";
import { entranceVariants } from "../styling/motionTokens.js";

export interface ResultPanelProps {
  readonly result: DemoResult | null;
}

const RESULT_ICON: Readonly<Record<ResultType, LucideIcon>> = {
  success: AlertTriangle,
  neutralized: CheckCircle2,
  info: Info,
} as const;

const RESULT_TITLE: Readonly<Record<ResultType, string>> = {
  success: "Attack Succeeded!",
  neutralized: "Violation Neutralized",
  info: "Info",
} as const;

/** Renders nothing when result is null — avoids empty panels. */
export const ResultPanel = React.memo(function ResultPanel({
  result,
}: ResultPanelProps): React.JSX.Element {
  const prefersReducedMotion = useReducedMotion() ?? false;

  const renderResult = (current: DemoResult): React.JSX.Element => {
    const style = RESULT_STYLE[current.type];
    const Icon = RESULT_ICON[current.type];
    const title = RESULT_TITLE[current.type];
    // Each distinct (type, message) replays the entrance — back-to-back
    // identical results stay still; new outcomes pop.
    const panelKey = `${current.type}:${current.message}`;
    const motionProps = prefersReducedMotion
      ? { initial: false as const }
      : {
          variants: entranceVariants,
          initial: "initial" as const,
          animate: "enter" as const,
          exit: "exit" as const,
        };

    return (
      <motion.div
        key={panelKey}
        data-testid="result-panel"
        {...motionProps}
        style={{
          marginTop: "1.5rem",
          padding: "1rem",
          borderRadius: "0.5rem",
          border: `1px solid ${style.border}`,
          backgroundColor: style.bg,
        }}
      >
        <div
          style={{
            fontWeight: "bold",
            color: style.titleColor,
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
          }}
        >
          <Icon aria-hidden="true" size={18} />
          <span>{title}</span>
        </div>

        <p style={{ marginTop: "0.5rem", color: GRAY[700] }}>{current.message}</p>

        {current.explanation !== undefined && (
          <details style={{ marginTop: "1rem" }}>
            <summary style={{ cursor: "pointer", fontSize: "0.875rem", fontWeight: "500" }}>
              Why? (click to expand)
            </summary>
            <div
              style={{
                marginTop: "0.5rem",
                fontSize: "0.875rem",
                padding: "0.75rem",
                backgroundColor: GRAY[50],
                borderRadius: "0.25rem",
                border: `1px solid ${GRAY[300]}`,
              }}
            >
              {current.explanation}
            </div>
          </details>
        )}

        {current.technicalDetail !== undefined && (
          <div
            style={{
              marginTop: "0.75rem",
              fontFamily: FONT.mono,
              fontSize: "0.75rem",
              color: GRAY[600],
              backgroundColor: GRAY[50],
              padding: "0.5rem",
              borderRadius: "0.25rem",
              border: `1px solid ${GRAY[300]}`,
            }}
          >
            {current.technicalDetail}
          </div>
        )}
      </motion.div>
    );
  };

  return (
    <AnimatePresence mode="wait">{result === null ? null : renderResult(result)}</AnimatePresence>
  );
});
